import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';

interface BetaReadinessCheckinModalProps {
  dayNumber: number;
  daysRemaining: number;
  shopId: string;
  onClose: () => void;
}

export const BetaReadinessCheckinModal = ({
  dayNumber,
  daysRemaining,
  shopId,
  onClose,
}: BetaReadinessCheckinModalProps) => {
  const [blocking, setBlocking] = useState<string>('no');
  const [blockersText, setBlockersText] = useState('');
  const [stabilityRating, setStabilityRating] = useState('3');
  const [improvementNotes, setImprovementNotes] = useState('');
  const [shopReady, setShopReady] = useState<string>('no');
  const [submitting, setSubmitting] = useState(false);
  const [snoozing, setSnoozing] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const { error } = await supabase.rpc('submit_beta_checkin', {
        p_shop_id: shopId,
        p_day_number: dayNumber,
        p_days_remaining: daysRemaining,
        p_blocking: blocking === 'yes',
        p_blockers_text: blocking === 'yes' ? blockersText : null,
        p_stability_rating: parseInt(stabilityRating),
        p_improvement_notes: improvementNotes || null,
        p_shop_ready_yes: shopReady === 'yes',
      });

      if (error) throw error;
      toast.success('Check-in submitted — thank you!');
      sessionStorage.setItem(`beta-checkin-dismissed-${shopId}`, 'true');
      onClose();
    } catch (err: any) {
      console.error('Beta checkin error:', err);
      toast.error(err.message || 'Failed to submit check-in');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSnooze = async () => {
    setSnoozing(true);
    try {
      const { error } = await supabase.rpc('snooze_beta_checkin', {
        p_shop_id: shopId,
      });
      if (error) throw error;
      toast.info('Reminder snoozed for 1 day');
      sessionStorage.setItem(`beta-checkin-dismissed-${shopId}`, 'true');
      onClose();
    } catch (err: any) {
      console.error('Snooze error:', err);
      toast.error(err.message || 'Failed to snooze');
    } finally {
      setSnoozing(false);
    }
  };

  const handleDismiss = () => {
    sessionStorage.setItem(`beta-checkin-dismissed-${shopId}`, 'true');
    onClose();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && handleDismiss()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            Beta Readiness Check-in
            <Badge variant="outline">Day {dayNumber} of 60</Badge>
          </DialogTitle>
          <DialogDescription>
            {daysRemaining} days remaining — quick pulse check on your experience.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* Blocking Issues */}
          <div className="space-y-2">
            <Label className="font-medium">Are there any blocking issues preventing daily use?</Label>
            <RadioGroup value={blocking} onValueChange={setBlocking} className="flex gap-4">
              <div className="flex items-center gap-2">
                <RadioGroupItem value="yes" id="blocking-yes" />
                <Label htmlFor="blocking-yes">Yes</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="no" id="blocking-no" />
                <Label htmlFor="blocking-no">No</Label>
              </div>
            </RadioGroup>
          </div>

          {blocking === 'yes' && (
            <div className="space-y-2">
              <Label htmlFor="blockers-text">What's blocking you?</Label>
              <Textarea
                id="blockers-text"
                placeholder="Describe the issue(s)..."
                value={blockersText}
                onChange={(e) => setBlockersText(e.target.value)}
                rows={3}
              />
            </div>
          )}

          {/* Stability Rating */}
          <div className="space-y-2">
            <Label>How stable has the platform been? (1 = very unstable, 5 = rock solid)</Label>
            <Select value={stabilityRating} onValueChange={setStabilityRating}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5].map((n) => (
                  <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Improvement Notes */}
          <div className="space-y-2">
            <Label htmlFor="improvement">What could we improve?</Label>
            <Textarea
              id="improvement"
              placeholder="Optional feedback..."
              value={improvementNotes}
              onChange={(e) => setImprovementNotes(e.target.value)}
              rows={3}
            />
          </div>

          {/* Go-live sentiment */}
          <div className="space-y-2">
            <Label className="font-medium">Do you feel your shop is ready for Go-Live?</Label>
            <RadioGroup value={shopReady} onValueChange={setShopReady} className="flex gap-4">
              <div className="flex items-center gap-2">
                <RadioGroupItem value="yes" id="ready-yes" />
                <Label htmlFor="ready-yes">Yes</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="no" id="ready-no" />
                <Label htmlFor="ready-no">Not yet</Label>
              </div>
            </RadioGroup>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button onClick={handleSubmit} disabled={submitting} className="flex-1">
              {submitting ? 'Submitting...' : 'Submit Check-in'}
            </Button>
            <Button variant="outline" onClick={handleSnooze} disabled={snoozing}>
              {snoozing ? '...' : 'Remind Tomorrow'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
